import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MenuSettingsService } from '@layout/left-panel/services/menu-settings.service';
import { LoadMenuSettings, LoadMenuSettingsIfNotLoaded, UpdateMenuSettings } from '@layout/left-panel/store/actions/menu-settings.actions';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

interface MenuSettingsStateModel {
  isMenuCollapsed: boolean;
  isLoaded: boolean;
  isLoading: boolean;
}
@State<MenuSettingsStateModel>({
  name: 'menuSettings',
  defaults: {
    isMenuCollapsed: false,
    isLoaded: false,
    isLoading: false,
  },
})
@Injectable()
export class MenuSettingsState {
  private readonly menuSettingsService = inject(MenuSettingsService);

  @Selector()
  static isMenuCollapsed({ isMenuCollapsed }: MenuSettingsStateModel): boolean {
    return isMenuCollapsed;
  }

  @Selector()
  static isLoaded({ isLoaded }: MenuSettingsStateModel): boolean {
    return isLoaded;
  }

  @Action(LoadMenuSettingsIfNotLoaded)
  loadMenuSettingsIfNotLoaded(context: StateContext<MenuSettingsStateModel>): void {
    const { isLoaded, isLoading } = context.getState();

    if (isLoaded || isLoading) {
      return;
    }

    context.dispatch(new LoadMenuSettings());
  }

  @Action(LoadMenuSettings, { cancelUncompleted: true })
  loadMenuSettings(context: StateContext<MenuSettingsStateModel>): Observable<boolean> {
    context.patchState({ isLoading: true });

    return this.menuSettingsService.getMenuSettings().pipe(
      tap(isMenuCollapsed => context.patchState({ isMenuCollapsed, isLoaded: true })),
      finalize(() => context.patchState({ isLoading: false }))
    );
  }

  @Action(UpdateMenuSettings, { cancelUncompleted: true })
  updateMenuSettings(context: StateContext<MenuSettingsStateModel>, { isMenuCollapsed }: UpdateMenuSettings): Observable<void> {
    context.patchState({ isMenuCollapsed });

    return this.menuSettingsService.updateMenuSettings(isMenuCollapsed).pipe(
      catchError((error: HttpErrorResponse) => {
        context.patchState({ isMenuCollapsed: !isMenuCollapsed });

        return throwError(() => error);
      })
    );
  }
}
